import Vue from 'vue';
import Router from 'vue-router';
import Meta from 'vue-meta';
import helper from '../util/helpers';

import { store } from '../store/store';
import Workplace from './subRoutes/workplace';
import { ConsultDesk, Icomsys } from './subRoutes/consultDesk';
import Member from './subRoutes/member';
import Webchat from './subRoutes/webchat';
import Cafe24 from './subRoutes/cafe24';
import Chatbot from './subRoutes/chatbot';
import Survey from './subRoutes/survey';
import Popup from './subRoutes/popup';
import conversationView from './subRoutes/conversationView';
import callChannelType from '@/constants/callChannelType';

import { BrandMenu, WorkplaceMenu, ConsultDesk as ConsultDeskMenu } from '@/constants/sidebarMenu';
import { getCartiniWorkplaceId } from '@/constants/customInfo';
import { checkTokenNeeded } from '@/util/custom';

import Nprogress from 'nprogress';
import Util from '@/util';

Vue.use(Router);
Vue.use(Meta);

const router = new Router({
  mode: 'history',
  routes: [
    {
      name: 'WorkplaceLayout',
      path: '/',
      component: () => import(/* webpackChunkName: "WorkplaceLayout" */ '@/components/workplace/layout'),
      children: [...Workplace, ...Webchat]
    },
    {
      name: 'consultdeskLayout',
      path: '/',
      component: () => import(/* webpackChunkName: "consultdeskLayout" */ '@/components/consultdesk/layout'),
      children: [...ConsultDesk, ...Icomsys]
    },
    {
      name: 'Public',
      path: '/',
      component: {
        template: '<router-view></router-view>'
      },
      children: [...Member, ...Cafe24, ...Chatbot, ...Survey, ...Popup, ...conversationView]
    },
    // catch all redirect
    { path: '*', redirect: '/' }
  ],
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        selector: to.hash,
        offset: { x: 0, y: 30 },
        behavior: 'smooth'
      };
    }
    return { x: 0, y: 0 };
  }
});

const getAuthMenuList = () => {
  return Util.getMenuList(BrandMenu).concat(Util.getMenuList(WorkplaceMenu));
};

const getRoleMenuList = () => {
  return Util.getMenuListWithRole(Object.entries(ConsultDeskMenu));
};

const authMenuList = getAuthMenuList();
const roleMenuList = getRoleMenuList();

router.beforeEach((to, from, next) => {
  Nprogress.start();
  const isValidToken = checkValidToken();
  const defaultRedirectPath = Workplace[0].path; // 대시보드
  const goToHome = () => {
    const referrer = window.document.referrer || null;
    defaultRedirectPath !== to.path
      ? next({ path: defaultRedirectPath, query: referrer === 'https://sell.smartstore.naver.com/' ? to.query : null })
      : next();
  };
  const hasCallChannel = () => {
    const tokenInfo = Util.parseLoginToken();
    return tokenInfo && tokenInfo.callServerType;
  };
  const isNeomecca = () => {
    const callType = Util.getCallTypeFromToken();
    return callType === callChannelType.NEOMECCA;
  };
  try {
    if (isValidToken) {
      // TODO: router로직 메뉴트리 가져오는 방식 변경에 맞게 변경 필요
      let agentMenus = JSON.parse(localStorage.getItem('agent-authority'));
      if (to.path.toLowerCase() === '/member/login' && Util.parseLoginToken().accountId && agentMenus) {
        next(defaultRedirectPath);
      }

      if (to.meta.requiresAuth !== false) {
        if (to.hash.indexOf('tab-chat-room') === -1) {
          const isCartini = getCartiniWorkplaceId().indexOf(Util.parseLoginToken().workPlaceId) !== -1;
          if (isCartini) {
            checkTokenNeeded();
          }
        }
      }
      getAgentMenus(
        router,
        to,
        next,
        () => {
          const brandList = store.getters.getBrands;
          if (to.meta.requiresAuth === false) {
            // 퍼블릭한 페이지 이동은 가능한 기존스펙 유지
            next();
          } else if (!brandList || brandList.length === 0) {
            // 할당된 브랜드 없으면 워크플레이스 GNB, 상담데스크, 브랜드 리스트 메뉴 어디에도 들어갈수 없음
            goToHome();
          } else {
            if (to.meta.requiresBrandKey && !to.query.brandIdentifier) {
              router.replace(defaultRedirectPath);
            } else {
              // 권한 자체가 세팅 안되었으면 대시보드로 이동 (모든 권한이 이용가능한 페이지)
              const accountType = store.getters.getUserDetails && store.getters.getUserDetails.accountsType;
              if (!accountType) {
                goToHome();
              } else {
                // 상담데스크 메뉴는 역할로 접근권한 체크
                const roleMenuInfo = roleMenuList.find((menu) => to.path.startsWith(menu.path));
                if (roleMenuInfo) {
                  const accountMenuInfo = store.getters.getBrandAuthority && store.getters.getBrandAuthority[roleMenuInfo.key];
                  // 역할로 접근권한 체크
                  if (!accountMenuInfo) {
                    const workplace = store.getters.getWorkplace;
                    // workplaceId 비교
                    const isKatini =
                      workplace &&
                      workplace.workPlaceInfo &&
                      workplace.workPlaceInfo.id &&
                      getCartiniWorkplaceId().indexOf(workplace.workPlaceInfo.id) !== -1;
                    if (!isKatini) {
                      goToHome();
                    } else {
                      // 카티니 워크플레이스인경우 API에서 내려주는 역할 없어도 접근 가능하게 처리
                      to.path === '/consultation' ? next() : goToHome();
                    }
                  } else {
                    next();
                  }
                } else {
                  // 워크플레이스 GNB, 브랜드 리스트 메뉴는 권한으로 접근권한 체크
                  const authMenuInfo = authMenuList.find((menu) => to.path.startsWith(menu.path));
                  if (authMenuInfo && authMenuInfo.accountsTypes && authMenuInfo.accountsTypes.indexOf(accountType) !== -1) {
                    // 실제 접근 가능함 여부 체크 이후에 추가 조건들로 대시보드 이동하는 케이스 작성
                    if (to.meta.requiresBrandKey && !brandList.some((data) => data.id === Number(to.query.brandIdentifier))) {
                      // 브랜드 아이디 필요한 화면에 존재하지 않는 브랜드 아이디로 접근 시 대시보드 이동 (= 서버 API 에서 주는 최신 브랜드 리스트에 존재하는지 체크) (= 화면 접근 막음)
                      goToHome();
                    } else if (to.path === '/agent/call_consultant_setting' && !hasCallChannel()) {
                      // 콜 내선번호 관리 화면 연동된 콜 채널 없는 경우 대시보드 이동 (= 화면 접근 막음)
                      goToHome();
                    } else if (to.path === '/agent/call_deflect_setting' && !isNeomecca()) {
                      // 콜 자동 배정 설정은 Neomecca 채널만 접근 가능
                      goToHome();
                    } else {
                      next();
                    }
                  } else {
                    goToHome();
                  }
                }
              }
            }
          }
        },
        () => {
          store.dispatch('logout').then(() => {
            next('/member/login');
          });
        }
      );
    } else if (to.meta.requiresAuth === false) {
      next();
    } else {
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('agent-authority');
      next('/member/login');
    }
  } catch (e) {
    console.log(e);
  }
});

router.afterEach((to, from) => {
  Nprogress.done();
});

const checkValidToken = () => {
  const now = helper.convertTimeToMomentType(helper.getCurrentDateTimeAsFormatted());
  return !!localStorage.getItem('token') && now.isBefore(helper.convertTimeToMomentType(Util.parseLoginToken().expireTime));
};

const getAgentMenus = (router, to, next, callback, catchCallback) => {
  // 토큰 세팅
  store.commit('setUserDetails', Util.parseLoginToken());

  if (!localStorage.getItem('agent-authority')) {
    store.dispatch('getMyMenu', { http: router.app.$http, catchCallback }).then(() => {
      callback();
    });
  } else {
    setCurrentMenuPath(to);
    store.commit('setAgentAuthority', JSON.parse(localStorage.getItem('agent-authority')));
    callback();
  }
};

function setCurrentMenuPath(to) {
  let path = [];
  for (let menu of to.matched) {
    if ('title' in menu.meta) {
      path.push(menu.meta.title);
    }
  }
  store.commit('setMenuPath', path.join(' > '));
}

export default router;



// WEBPACK FOOTER //
// ./src/router/index.js